import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'

/**
 * OAuthCallbackPage
 *
 * Handles the redirect after an OAuth flow (Google, GitHub).
 * Reads `?code=` + `?state=` from the URL and exchanges them for a session.
 * Also handles SSO token redirect from lukhas.id: `?token=<sso-token>`
 *
 * Apple uses the popup flow and never lands here — this is for Google/GitHub.
 */
export default function OAuthCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { validateSSOToken } = useAuthStore() as any
  const [status, setStatus] = useState<'processing' | 'error'>('processing')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    const token = searchParams.get('token')
    const code = searchParams.get('code')
    const error = searchParams.get('error')

    if (error) {
      setErrorMsg(decodeURIComponent(error))
      setStatus('error')
      return
    }

    // SSO token from lukhas.id cross-domain redirect
    if (token) {
      if (typeof validateSSOToken === 'function') {
        validateSSOToken(token)
          .then(() => {
            const intent = sessionStorage.getItem('oauth_intent') || '/dashboard'
            sessionStorage.removeItem('oauth_intent')
            navigate(intent, { replace: true })
          })
          .catch((err: Error) => {
            setErrorMsg(err.message || 'Authentication failed')
            setStatus('error')
          })
      } else {
        navigate('/dashboard', { replace: true })
      }
      return
    }

    // OAuth code — let the backend handle the exchange
    if (code) {
      const intent = sessionStorage.getItem('oauth_intent') || '/dashboard'
      sessionStorage.removeItem('oauth_intent')
      // For now, navigate to the intent URL (backend session will have been set via cookie)
      navigate(intent, { replace: true })
      return
    }

    // No token or code — go to login
    navigate('/login', { replace: true })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-sm">
          <p className="text-white/70 text-sm">Authentication failed</p>
          <p className="text-red-400/80 text-xs">{errorMsg}</p>
          <button
            onClick={() => navigate('/login')}
            className="text-teal-400 hover:text-teal-300 text-sm transition-colors"
          >
            Return to sign in
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-8 h-8 border-2 border-teal-500/50 border-t-teal-400 rounded-full animate-spin mx-auto" />
        <p className="text-white/50 text-sm font-light tracking-wide">Completing sign in…</p>
      </div>
    </div>
  )
}
